import { LOCAL_STORAGE_KEY } from '@common/constants';

export const getAuthToken = (): string | null => {
    // these three items have been user configurable but are being deprecated for cloudfront
    // I'm leaving the functions here for now
    [
        LOCAL_STORAGE_KEY.BEARER_TOKEN,
        LOCAL_STORAGE_KEY.BASE_URL,
        LOCAL_STORAGE_KEY.SECTIONS,
        LOCAL_STORAGE_KEY.LOAD_ESTIMATE,
        LOCAL_STORAGE_KEY.USER_TOKEN,
    ].forEach((key) => {
        localStorage.removeItem(key);
    });

    // split out the query string and save in local storage
    const params = new URL(location.href).searchParams;
    params.forEach((value, key) => {
        if (key && value) {
            localStorage.setItem(key, value); // use setItem because there may be keys we don't know about
        }
    });

    // if the token was set in query string, get it back from localStorage
    const token = localStorage.getItem(LOCAL_STORAGE_KEY.BEARER_TOKEN);

    return token ? `Bearer ${token}` : null;
};

export const setStorageData = (
    key: LOCAL_STORAGE_KEY,
    data: unknown,
    replacer?: ((this: unknown, key: string, value: unknown) => unknown) | undefined
): void => {
    localStorage.setItem(key, JSON.stringify(data, replacer));
};

const QUOTE_REGEXP = /^"|"$/g;

/**
 * Sanitizes stored data before returning it
 * @param data the data to be sanitized
 * @param reviver custom function to parse the data
 * @returns the sanitized data, `undefined` in case of a parsing error
 */
export const sanitizeStorageValue = <T>(
    data: string,
    reviver?: (this: unknown, key: string, value: unknown) => unknown
): T | undefined => {
    try {
        const isJson = data.startsWith('{') || data.startsWith('[');

        return isJson ? (JSON.parse(data, reviver) as T) : (data.replace(QUOTE_REGEXP, '') as T);
    } catch (error) {
        console.error('Error parsing localStorage data:', error);

        return undefined;
    }
};

/**
 * Retrieves values from the `localStorage`
 * @param key a key from the `LOCAL_STORAGE_KEY` enum
 * @param reviver a custom function to retrieve a stored value
 * @returns the stored value or `null`
 */
export const getStorageData = <T>(
    key: LOCAL_STORAGE_KEY,
    reviver?: (this: unknown, key: string, value: unknown) => unknown
): T | undefined => {
    const data = localStorage.getItem(key);

    return data ? sanitizeStorageValue<T>(data, reviver) : undefined;
};

export const removeStorageData = (key: LOCAL_STORAGE_KEY): void => {
    localStorage.removeItem(key);
};
